import { t } from '@ctcells/i18n'
import type { ReactNode } from 'react'

import '../atoms/atoms.css' // .a-sr(시각적 숨김) — 이 파일은 원자를 쓰지 않아 직접 건다

import './layout.css'

export interface SidePanelProps {
  /**
   * 머리줄 왼쪽의 제목(「원심분리기 상세」). 🔴 **안 넘기면 종전 그대로 제목이 없다**(§14-3) — 넘기는 곳은 장비별 보기
   * 상세 카드 하나다(디자인팀 시안 `equipView()` · 2026-10-02). 그림 탭 화면 · 패드는 탭이 이름을 말한다.
   */
  title?: string | undefined
  /**
   * 머리줄 왼쪽 배지. 🔴 **제목을 넣는 자리가 아니다** —
   * 사이드 패널에는 제목이 없다(§14-3). 어떤 컴포넌트인지는 탭이 말한다.
   */
  badge?: ReactNode
  /** 닫기. 없으면 닫기 버튼 자체가 안 나온다(패드처럼 항상 열려 있는 자리) */
  onClose?: () => void
  children: ReactNode
}

/**
 * 모니터링 · 패드 사이드 패널 — `.sidepanel`(CSS 874행) · README §14-3.
 *
 * 🔴 **제어 버튼을 넣지 마라.** 조작은 개발 › 제어에서만 한다(§1-3).
 *    maintenance 역할에게는 「정비 제어는 제어 화면에서 →」 링크만 둔다.
 * 🔴 **제목이 없다.** 배지와 × 만 있는 머리줄이다.
 *
 * 구역 순서는 고정이다: 현재 메트릭(2열 격자) · 진행(3구간) · 챔버 표 · 정보.
 * 구역 사이에 구분선이 들어간다(`.sps + .sps` 879행).
 */
export function SidePanel({ title, badge, onClose, children }: SidePanelProps) {
  return (
    <aside className="sidepanel" aria-label={t('panel.aria')}>
      <div className="sidepanel__head">
        {title === undefined ? null : <h3 className="sidepanel__title">{title}</h3>}
        <div className="sidepanel__badge">{badge}</div>
        {onClose ? (
          <button type="button" className="sidepanel__close" onClick={onClose}>
            <span aria-hidden="true">×</span>
            <span className="a-sr">{t('action.close')}</span>
          </button>
        ) : null}
      </div>
      {children}
    </aside>
  )
}

/**
 * 사이드 패널 안 한 구역. 구분선은 CSS 가 인접 형제에만 넣는다 — 첫 구역 위에는 선이 없다.
 * `badge` 를 주면 제목 줄 오른쪽에 선다(6축 로봇 상태 칩 · eric 2026-10-12) — 제목 글자(`.sect__title`)와 따로라 제목 글자가 그대로다.
 */
export function SidePanelSection({
  title,
  badge,
  children,
}: {
  title: string
  badge?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="sidepanel__sect">
      {badge === undefined ? (
        <div className="sect__title">{title}</div>
      ) : (
        <div className="sect__head">
          <div className="sect__title">{title}</div>
          <span className="sect__badge">{badge}</span>
        </div>
      )}
      {children}
    </section>
  )
}
